import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { FormField, FormItem, FormLabel } from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  eachMonthOfInterval,
  endOfYear,
  format,
  startOfYear,
  isBefore,
  startOfDay,
} from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { ko } from "date-fns/locale";
import { cn } from "@/libs/utils";

type ControlledDatePickerProps = {
  label: string;
  name: string;
  placeholder: string;
  labelClassName?: string;
  triggerClassName?: string;
  iconClassName?: string;
  placeholderClassName?: string;
};

export default function ControlledDatePicker(props: ControlledDatePickerProps) {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={props.name}
      render={({ field }) => (
        <ControlledDatePickerInner {...props} field={field} />
      )}
    />
  );
}

function ControlledDatePickerInner({
  field,
  label,
  placeholder,
  labelClassName,
  triggerClassName,
  iconClassName,
  placeholderClassName,
}: ControlledDatePickerProps & {
  field: any;
}) {
  const initialDate = field.value ? new Date(field.value) : undefined;
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    initialDate
  );
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(
    initialDate?.getMonth() ?? new Date().getMonth()
  );
  const [year, setYear] = useState(
    initialDate?.getFullYear() ?? new Date().getFullYear()
  );

  useEffect(() => {
    const nextValue = field.value ? new Date(field.value) : undefined;
    setSelectedDate(nextValue);

    if (nextValue) {
      setMonth(nextValue.getMonth());
      setYear(nextValue.getFullYear());
    }
  }, [field.value]);

  const years = useMemo(() => {
    const next3Year = new Date().getFullYear() + 3;
    return Array.from(
      { length: next3Year - 2000 + 1 },
      (_, i) => next3Year - i
    );
  }, []);

  const months = useMemo(() => {
    return eachMonthOfInterval({
      start: startOfYear(new Date(year, 0, 1)),
      end: endOfYear(new Date(year, 0, 1)),
    });
  }, [year]);

  const handleYearChange = (selectedYear: string) => {
    const newYear = parseInt(selectedYear, 10);
    setYear(newYear);
    const newDate = new Date(newYear, month, 1);
    setSelectedDate(newDate);
    field.onChange(newDate);
  };

  const handleMonthChange = (selectedMonth: string) => {
    const newMonth = parseInt(selectedMonth, 10);
    setMonth(newMonth);
    const newDate = new Date(year, newMonth, 1);
    setSelectedDate(newDate);
    field.onChange(newDate);
  };

  return (
    <FormItem>
      <FormLabel className={labelClassName}>{label}</FormLabel>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full h-11 px-3 flex items-center justify-between gap-3 border-gray-C",
              selectedDate ? "text-gray-A" : "text-gray-B",
              triggerClassName
            )}
          >
            {selectedDate ? (
              format(selectedDate, "yyyy-MM-dd")
            ) : (
              <span className={cn("text-sm font-normal", placeholderClassName)}>
                {placeholder}
              </span>
            )}
            <CalendarIcon className={cn("h-4 w-4", iconClassName)} />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="flex justify-between p-2 space-x-1">
            <Select onValueChange={handleYearChange} value={year.toString()}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {years.map((y) => (
                  <SelectItem key={y} value={y.toString()}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select onValueChange={handleMonthChange} value={month.toString()}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {months.map((m, index) => (
                  <SelectItem key={index} value={index.toString()}>
                    {format(m, "MMMM", { locale: ko })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Calendar
            key={`${year}-${month}`}
            mode="single"
            selected={selectedDate}
            onSelect={(date) => {
              if (date) {
                setSelectedDate(date);
                setMonth(date.getMonth());
                setYear(date.getFullYear());
                field.onChange(date);
                setOpen(false);
              }
            }}
            month={new Date(year, month)}
            onMonthChange={(newMonth) => {
              setMonth(newMonth.getMonth());
              setYear(newMonth.getFullYear());
            }}
            disabled={(date) => isBefore(date, startOfDay(new Date()))}
            locale={ko}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </FormItem>
  );
}
